import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CodeIcon from "@material-ui/icons/Code";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import WarningIcon from "@material-ui/icons/Warning";
import RefreshIcon from "@material-ui/icons/Refresh";
import _ from "lodash";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { indentUnit } from "@codemirror/language";
import { EditorState } from "@codemirror/state";
import { Decoration, EditorView, keymap, lineNumbers, ViewPlugin } from "@codemirror/view";
import { highlight as prismHighlight, languages, tokenize } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "./containerPlayEngineScripts.scss";
import { sendCommand } from "../modules/playEngineBridge";

const CARD_BG = "#474747";
const FIELD_BG = "#313131";
const GUTTER_BG = "#2a2a2a";
const CODE_FONT = "'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";
const CODE_SIZE = 12;
const CODE_LINE_HEIGHT = 18;

function highlightJavaScript(code) {
  return prismHighlight(code, languages.js);
}

function prismDecorations(code) {
  const ranges = [];
  let position = 0;

  function visit(nodes) {
    nodes.forEach((node) => {
      if (typeof node === "string") {
        position += node.length;
        return;
      }

      const from = position;
      visit(Array.isArray(node.content) ? node.content : [node.content]);
      if (position > from) {
        ranges.push(Decoration.mark({ class: `cm-prism-${node.type}` }).range(from, position));
      }
    });
  }

  visit(tokenize(code, languages.js));
  return Decoration.set(ranges, true);
}

const prismHighlightPlugin = ViewPlugin.fromClass(
  class {
    constructor(view) {
      this.decorations = prismDecorations(view.state.doc.toString());
    }

    update(update) {
      if (update.docChanged) this.decorations = prismDecorations(update.state.doc.toString());
    }
  },
  { decorations: (plugin) => plugin.decorations }
);

const scriptEditorTheme = EditorView.theme({
  "&": {
    color: "#ddd",
    backgroundColor: FIELD_BG,
    border: "1px solid #666",
    borderRadius: "4px",
    height: "320px",
    maxHeight: "560px",
    fontFamily: CODE_FONT,
    fontSize: `${CODE_SIZE}px`,
  },
  ".cm-content": {
    minHeight: "100%",
    padding: "8px 12px",
    fontFamily: CODE_FONT,
    fontSize: `${CODE_SIZE}px`,
    lineHeight: `${CODE_LINE_HEIGHT}px`,
    caretColor: "#fff",
  },
  ".cm-gutters": {
    backgroundColor: GUTTER_BG,
    color: "#7a7a7a",
    border: "none",
  },
  ".cm-lineNumbers .cm-gutterElement": { padding: "0 8px 0 10px" },
  "&.cm-focused": { outline: "none" },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * A ScriptDef's `source` is an array of lines (the readable snapshot form) or a
 * single string. Both are valid — see ScriptModule/types.ts — so every reader
 * has to accept either.
 */
function sourceToText(source) {
  if (Array.isArray(source)) return source.join("\n");
  if (typeof source === "string") return source;
  return "";
}

/**
 * The hash the authoring tools write: `sha256:<first 16 hex of sha256(source)>`
 * over the joined source (snapkit's `scriptDef()` does exactly this).
 *
 * It exists to detect drift against extracted `.logic.js` mirrors, so a STALE
 * hash is worse than none: it would claim "no drift" for a script that moved.
 * When the browser cannot hash (crypto.subtle needs a secure context) we drop
 * the field rather than carry the old value forward.
 */
async function hashSource(text) {
  try {
    if (!window.crypto?.subtle) return null;
    const bytes = new TextEncoder().encode(text);
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return `sha256:${hex.slice(0, 16)}`;
  } catch (_err) {
    return null;
  }
}

/** Findings for one script id, newest report wins. */
function findingsFor(report, scriptId) {
  const entry = _.find(_.get(report, "scripts", []), (s) => s.scriptId === scriptId);
  return _.get(entry, "findings", []);
}

// ─── Read-only source view ────────────────────────────────────────────────────
/**
 * Line-numbered, scrollable, syntax-highlighted source.
 */
function SourceView({ text, highlightLines }) {
  const lines = text.split("\n");
  const marked = new Set(highlightLines || []);

  return (
    <div
      style={{
        display: "flex",
        background: FIELD_BG,
        borderRadius: 4,
        maxHeight: 480,
        overflow: "auto",
        fontFamily: CODE_FONT,
        fontSize: CODE_SIZE,
        lineHeight: `${CODE_LINE_HEIGHT}px`,
      }}
    >
      <div
        style={{
          background: GUTTER_BG,
          color: "#7a7a7a",
          padding: "8px 8px 8px 10px",
          textAlign: "right",
          userSelect: "none",
          position: "sticky",
          left: 0,
        }}
      >
        {lines.map((_l, i) => (
          <div key={i} style={{ color: marked.has(i + 1) ? "#ff8a80" : undefined }}>
            {i + 1}
          </div>
        ))}
      </div>
      <div
        style={{
          position: "relative",
          flex: 1,
          width: "max-content",
          minWidth: "100%",
        }}
      >
        {lines.map((_line, i) => marked.has(i + 1) && (
          <div
            key={i}
            style={{
              position: "absolute",
              zIndex: 0,
              top: 8 + i * CODE_LINE_HEIGHT,
              left: 0,
              right: 0,
              height: CODE_LINE_HEIGHT,
              background: "rgba(255,82,82,0.14)",
              pointerEvents: "none",
            }}
          />
        ))}
        <pre
          className="playengine-script-source"
          style={{
            position: "relative",
            zIndex: 1,
            margin: 0,
            padding: "8px 12px",
            color: "#ddd",
            whiteSpace: "pre",
            fontFamily: "inherit",
            fontSize: "inherit",
            lineHeight: "inherit",
          }}
          dangerouslySetInnerHTML={{ __html: `${highlightJavaScript(text)}${text.endsWith("\n") ? " " : ""}` }}
        />
      </div>
    </div>
  );
}

// ─── Findings list ────────────────────────────────────────────────────────────
function FindingsList({ findings }) {
  if (!findings.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      {findings.map((f, i) => {
        const isError = f.severity === "error";
        return (
          <div
            key={`${f.rule}-${f.line}-${i}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 6,
              padding: "4px 8px",
              marginBottom: 4,
              borderRadius: 4,
              background: isError ? "rgba(244,67,54,0.12)" : "rgba(255,193,7,0.10)",
            }}
          >
            {isError ? (
              <ErrorOutlineIcon style={{ fontSize: 16, color: "#ef5350", marginTop: 2 }} />
            ) : (
              <WarningIcon style={{ fontSize: 16, color: "#ffb300", marginTop: 2 }} />
            )}
            <Typography variant="body2" style={{ color: "#ddd", fontSize: 12 }}>
              <span style={{ fontFamily: CODE_FONT, color: "#aaa" }}>line {f.line}</span>
              {" · "}
              <span style={{ color: "#999" }}>{f.rule}</span>
              {" — "}
              {f.message}
            </Typography>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * PlayEngine Scripts panel — read, edit and save the snapshot's `scripts[]`
 * (the engine's embedded gameplay layer).
 *
 * Saving goes through the engine on purpose: `playengine:set-script` validates
 * the source against the LIVE scene (component ids, tags, sounds, actions) and
 * only starts it when there are no errors. Persisting a script the engine
 * refused would ship a playable whose logic never runs, so a failed validation
 * keeps the editor open and shows the findings instead of writing to the
 * version.
 *
 * @param props.selectedVersion              {Object}
 * @param props.selectedBase                 {Object}
 * @param props.selectedGame                 {Object}
 * @param props.setSelectedVersionPathValue  {function}
 * @param props.userPermissions              {Object}
 * @param props.createSnackbar               {function}
 * @param props.isPreviewLoaded              {boolean}
 * @param props.iFrameId                     {string}
 */
export default function containerPlayEngineScripts(props) {
  const [scripts, setScripts] = useState(null); // null = loading
  const [apiVersion, setApiVersion] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [savingId, setSavingId] = useState(null);
  /** scriptId -> Finding[] from the last validate/save reply. */
  const [findings, setFindings] = useState({});
  /** scriptId -> { phase, message } from unsolicited runtime errors. */
  const [runtimeErrors, setRuntimeErrors] = useState({});

  const loadGenRef = useRef(0);
  const editorMountRef = useRef(null);
  const editorViewRef = useRef(null);
  const canEdit = props.userPermissions?.update !== false;
  const engineReachable = Boolean(props.iFrameId && props.isPreviewLoaded);

  // ── Load ────────────────────────────────────────────────────────────────
  const loadScripts = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setLoadError(null);

    // 1. Ask the engine — it is the only source that knows what is actually
    //    running (hot-reloaded edits included).
    if (engineReachable) {
      try {
        const resp = await sendCommand(props.iFrameId, { type: "playengine:get-scripts" });
        if (gen !== loadGenRef.current) return;
        if (Array.isArray(resp?.scripts)) {
          setScripts(resp.scripts);
          setApiVersion(resp.apiVersion ?? null);
          return;
        }
      } catch {
        if (gen !== loadGenRef.current) return;
        // fall through to the snapshot fallback
      }
    }

    // 2. Fall back to the persisted snapshot.
    const persisted = _.get(props, "selectedVersion.playEngineConfig.scripts");
    if (Array.isArray(persisted)) {
      setScripts(persisted);
    } else {
      setScripts([]);
    }
  }, [props.iFrameId, engineReachable, props.selectedVersion]);

  useEffect(() => {
    if (!props.selectedVersion) return undefined;
    setScripts(null);
    setEditingId(null);
    setFindings({});
    setRuntimeErrors({});
    loadScripts();
    // Retire this generation on unmount so a late reply cannot set state on a
    // dead component — switching versions unmounts these panels.
    return () => {
      loadGenRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedVersion, props.isPreviewLoaded, props.iFrameId]);

  // ── Runtime script errors (unsolicited push) ────────────────────────────
  // A script that throws at runtime never reaches the request/response path, so
  // without this the editor would show a clean report for a dead script.
  useEffect(() => {
    function handlePush(event) {
      const data = event?.data;
      if (data?.type !== "playengine:script-error") return;
      setRuntimeErrors((prev) => ({
        ...prev,
        [data.scriptId]: { phase: data.phase, message: data.message, detail: data.detail },
      }));
    }
    window.addEventListener("message", handlePush);
    return () => window.removeEventListener("message", handlePush);
  }, []);

  // ── Edit / cancel ───────────────────────────────────────────────────────
  const startEdit = useCallback((script) => {
    setDraft(sourceToText(script.source));
    setEditingId(script.id);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setDraft("");
  }, []);

  useEffect(() => {
    if (!editingId || !editorMountRef.current) return undefined;

    const state = EditorState.create({
      doc: draft,
      extensions: [
        lineNumbers(),
        history(),
        indentUnit.of("  "),
        keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
        prismHighlightPlugin,
        scriptEditorTheme,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) setDraft(update.state.doc.toString());
        }),
      ],
    });
    const view = new EditorView({ state, parent: editorMountRef.current });
    editorViewRef.current = view;

    return () => {
      view.destroy();
      if (editorViewRef.current === view) editorViewRef.current = null;
    };
    // The editor is initialized once per edit session; its own update listener
    // keeps the React draft synchronized without recreating the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  // ── Save ────────────────────────────────────────────────────────────────
  const saveScript = useCallback(async (script) => {
    if (!canEdit) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    if (!engineReachable) {
      props.createSnackbar?.("Preview must be loaded to validate and save a script.", "warning", false);
      return;
    }

    setSavingId(script.id);
    try {
      const text = draft;
      const hash = await hashSource(text);
      const nextDef = { ...script, source: text.split("\n") };
      if (hash) nextDef.hash = hash;
      else delete nextDef.hash; // stale hash would lie about drift

      const resp = await sendCommand(
        props.iFrameId,
        { type: "playengine:set-script", script: nextDef },
        15000
      );

      const report = resp?.report;
      setFindings((prev) => ({ ...prev, [script.id]: findingsFor(report, script.id) }));

      if (!resp?.success) {
        const errorCount = _.get(report, "errorCount", 0);
        props.createSnackbar?.(
          errorCount
            ? `Script not saved — ${errorCount} error${errorCount === 1 ? "" : "s"}. See the list below.`
            : `Script not saved — ${resp?.error || "the engine refused it"}.`,
          "error",
          false
        );
        return; // stay in edit mode; nothing is persisted
      }

      // The engine accepted and hot-reloaded it — now persist.
      const nextScripts = (scripts || []).map((s) => (s.id === script.id ? nextDef : s));
      setScripts(nextScripts);
      props.setSelectedVersionPathValue?.({
        path: "playEngineConfig.scripts",
        value: nextScripts,
      });

      // A fresh save clears whatever the previous run reported.
      setRuntimeErrors((prev) => _.omit(prev, script.id));
      setEditingId(null);
      setDraft("");

      const warningCount = _.get(report, "warningCount", 0);
      props.createSnackbar?.(
        warningCount
          ? `Script saved with ${warningCount} warning${warningCount === 1 ? "" : "s"}.`
          : "Script saved and reloaded.",
        warningCount ? "warning" : "success",
        false
      );
    } catch (err) {
      props.createSnackbar?.(`Script could not be saved: ${err.message}`, "error", false);
    } finally {
      setSavingId(null);
    }
  }, [canEdit, engineReachable, draft, scripts, props]);

  // ── Guards ──────────────────────────────────────────────────────────────
  // Every hook above this line runs unconditionally — nothing hook-shaped may
  // appear after an early return.
  if (_.isNil(props.selectedVersion) || _.isNil(props.selectedBase)) return null;

  const headerNote = apiVersion == null ? null : `ctx API v${apiVersion}`;

  if (scripts === null) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div style={{ padding: 8 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <CodeIcon style={{ color: "#bbb" }} />
        <Typography variant="body2" style={{ color: "#ddd", flexGrow: 1 }}>
          Snapshot scripts{headerNote ? ` · ${headerNote}` : ""}
        </Typography>
        <Tooltip title="Reload from the running playable">
          <span>
            <Button size="small" onClick={loadScripts} startIcon={<RefreshIcon />} style={{ color: "#bbb" }}>
              Reload
            </Button>
          </span>
        </Tooltip>
      </div>

      {loadError && (
        <Typography variant="body2" style={{ color: "#ef5350", marginBottom: 8 }}>
          {loadError}
        </Typography>
      )}

      {/* `set-script` replaces one script in the RUNNING playable and leaves
          whatever the previous version already did to the scene in place — the
          engine says so explicitly. Saying it here saves the "why is my old
          state still there" round trip. */}
      {engineReachable && scripts.length > 0 && (
        <Typography variant="caption" style={{ color: "#999", display: "block", marginBottom: 8 }}>
          Saving validates against the live scene and hot-reloads that script. Changes the previous
          version already made to the scene are not undone — reload the preview for a clean run.
        </Typography>
      )}

      {!engineReachable && (
        <Typography variant="caption" style={{ color: "#ffb300", display: "block", marginBottom: 8 }}>
          Preview is not loaded — showing the saved snapshot. Editing needs the preview, because a
          script is validated against the live scene before it is saved.
        </Typography>
      )}

      {scripts.length === 0 && (
        <Typography variant="body2" style={{ color: "#999" }}>
          This playable has no scripts.
        </Typography>
      )}

      {scripts.map((script) => {
        const isEditing = editingId === script.id;
        const isSaving = savingId === script.id;
        const text = isEditing ? draft : sourceToText(script.source);
        const lineCount = text.split("\n").length;
        const scriptFindings = findings[script.id] || [];
        const errorLines = scriptFindings.filter((f) => f.severity === "error").map((f) => f.line);
        const runtimeError = runtimeErrors[script.id];

        return (
          <Card key={script.id} style={{ background: CARD_BG, marginBottom: 12 }}>
            <CardContent>
              {/* Script header */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <Typography variant="body2" style={{ color: "#fff", fontFamily: CODE_FONT }}>
                  {script.id}
                </Typography>
                {script.enabled === false && (
                  <Chip size="small" label="disabled" style={{ background: "#5a5a5a", color: "#ddd" }} />
                )}
                <Chip
                  size="small"
                  label={`${lineCount} lines`}
                  style={{ background: FIELD_BG, color: "#bbb" }}
                />
                {script.apiVersion != null && (
                  <Chip
                    size="small"
                    label={`api v${script.apiVersion}`}
                    style={{ background: FIELD_BG, color: "#bbb" }}
                  />
                )}

                <div style={{ flexGrow: 1 }} />

                {!isEditing && (
                  <Tooltip
                    title={
                      !canEdit
                        ? "You do not have permission to update"
                        : !engineReachable
                          ? "Preview must be loaded"
                          : ""
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canEdit || !engineReachable}
                        onClick={() => startEdit(script)}
                        style={{ color: "#ddd", borderColor: "#666" }}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                )}

                {isEditing && (
                  <>
                    <Button size="small" onClick={cancelEdit} disabled={isSaving} style={{ color: "#bbb" }}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      onClick={() => saveScript(script)}
                      startIcon={isSaving ? <CircularProgress size={14} /> : null}
                    >
                      {isSaving ? "Saving" : "Save"}
                    </Button>
                  </>
                )}
              </div>

              {/* Source */}
              {isEditing ? (
                <div ref={editorMountRef} />
              ) : (
                <SourceView text={text} highlightLines={errorLines} />
              )}

              {/* Validation findings from the last save attempt */}
              <FindingsList findings={scriptFindings} />

              {/* Runtime failure pushed by the engine */}
              {runtimeError && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "6px 8px",
                    borderRadius: 4,
                    background: "rgba(244,67,54,0.12)",
                  }}
                >
                  <Typography variant="body2" style={{ color: "#ef5350", fontSize: 12 }}>
                    Runtime error ({runtimeError.phase}): {runtimeError.message}
                  </Typography>
                  {runtimeError.detail && (
                    <Typography
                      variant="caption"
                      style={{ color: "#bbb", fontFamily: CODE_FONT, whiteSpace: "pre-wrap" }}
                    >
                      {runtimeError.detail}
                    </Typography>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
